import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { AbstractContentComponent } from '../../../abstract-content.component';
import { Context } from '@service/context.service';
import { Column } from '@shared/table/column';
import { ChopLeistungsgruppe, ChopPunktekategorie } from '@entity/chop';
import { ChopService } from '@service/chop.service';
import { ChopLeistungsgruppeDialogComponent } from './chop-leistungsgruppe-dialog/chop-leistungsgruppe-dialog.component';
import { ChopPunktekategorieDialogComponent } from './chop-punktekategorie-dialog/chop-punktekategorie-dialog.component';
import { ActionButtonDirective } from '@shared/table/action-button.directive';
import { ToolbarButtonDirective } from '@shared/table/toolbar-button.directive';
import { TableComponent } from '@shared/table/table.component';
import { TabsModule } from 'primeng/tabs';
import { TranslatePipe } from '@ngx-translate/core';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { ChopAlterskategorieDetailData } from './chop-alterskategorie-detail.resolve';

@Component({
  selector: 'app-chop-alterskategorie-detail',
  templateUrl: './chop-alterskategorie-detail.component.html',
  styleUrls: ['./chop-alterskategorie-detail.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TabsModule, TranslatePipe, TableComponent, ToolbarButtonDirective, ActionButtonDirective],
})
export class ChopAlterskategorieDetailComponent extends AbstractContentComponent implements OnInit {
  private chopService = inject(ChopService);

  private data = this.activatedRoute.snapshot.data.detail as ChopAlterskategorieDetailData;
  private alterskategorie = this.data.alterskategorie;
  private katalog = this.data.katalog;
  leistungsgruppen: ChopLeistungsgruppe[] = this.data.leistungsgruppen;
  punktekategorien: ChopPunktekategorie[] = this.data.punktekategorien;
  leistungsgruppeColumns: Column[] = [];
  punktekategorieColumns: Column[] = [];
  activeTab = 'leistungsgruppen';
  private alterskategorieId = this.alterskategorie.id;

  constructor() {
    super(Context.Global);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.setColumns();
    this.refreshBreadcrumbs();
  }

  protected onLanguageChange(): void {
    this.setColumns();
    this.refreshBreadcrumbs();
    this.markForCheck();
  }

  reloadLeistungsgruppen(): void {
    this.chopService.getLeistungsgruppen(this.alterskategorieId).subscribe((leistungsgruppen) => {
      this.leistungsgruppen = leistungsgruppen;
      this.markForCheck();
    });
  }

  reloadPunktekategorien(): void {
    this.chopService.getPunktekategorien(this.alterskategorieId).subscribe((punktekategorien) => {
      this.punktekategorien = punktekategorien;
      this.markForCheck();
    });
  }

  showCreateLeistungsgruppe(): void {
    this.openLeistungsgruppeDialog()?.onClose.subscribe((result) => {
      if (result) {
        this.reloadLeistungsgruppen();
      }
    });
  }

  showEditLeistungsgruppe(lg: ChopLeistungsgruppe): void {
    this.openLeistungsgruppeDialog(lg)?.onClose.subscribe((result) => {
      if (result) {
        this.reloadLeistungsgruppen();
      }
    });
  }

  openLeistungsgruppeDetail(lg: ChopLeistungsgruppe): void {
    this.navigate(['/admin-chop/leistungsgruppen', lg.id]);
  }

  deleteLeistungsgruppe(lg: ChopLeistungsgruppe): void {
    this.confirm({
      header: this.translate('CHOP.DELETE_LEISTUNGSGRUPPE_HEADER'),
      message: this.translate('CHOP.DELETE_LEISTUNGSGRUPPE_MESSAGE', {
        nummer: lg.nummer,
        bezeichnung: this.getLocalizedText(lg.bezeichnung),
      }),
      accept: () => {
        this.chopService.deleteLeistungsgruppe(lg.id).subscribe({
          next: () => {
            this.showSuccessMessage();
            this.reloadLeistungsgruppen();
          },
          error: (err) => this.showErrorMessage(undefined, err?.error?.message),
        });
      },
    });
  }

  showCreatePunktekategorie(): void {
    this.openPunktekategorieDialog()?.onClose.subscribe((result) => {
      if (result) {
        this.reloadPunktekategorien();
      }
    });
  }

  showEditPunktekategorie(pk: ChopPunktekategorie): void {
    this.openPunktekategorieDialog(pk)?.onClose.subscribe((result) => {
      if (result) {
        this.reloadPunktekategorien();
      }
    });
  }

  deletePunktekategorie(pk: ChopPunktekategorie): void {
    this.confirm({
      header: this.translate('CHOP.DELETE_PUNKTEKATEGORIE_HEADER'),
      message: this.translate('CHOP.DELETE_PUNKTEKATEGORIE_MESSAGE', { code: pk.code }),
      accept: () => {
        this.chopService.deletePunktekategorie(pk.id).subscribe({
          next: () => {
            this.showSuccessMessage();
            this.reloadPunktekategorien();
          },
          error: (err) => this.showErrorMessage(undefined, err?.error?.message),
        });
      },
    });
  }

  private openLeistungsgruppeDialog(leistungsgruppe?: ChopLeistungsgruppe): DynamicDialogRef | null {
    return this.dialogService.open(ChopLeistungsgruppeDialogComponent, {
      modal: true,
      closable: true,
      focusOnShow: false,
      width: '500px',
      header: this.translate(leistungsgruppe ? 'CHOP.EDIT_LEISTUNGSGRUPPE' : 'CHOP.CREATE_LEISTUNGSGRUPPE'),
      data: { alterskategorieId: this.alterskategorieId, leistungsgruppe },
    });
  }

  private openPunktekategorieDialog(punktekategorie?: ChopPunktekategorie): DynamicDialogRef | null {
    return this.dialogService.open(ChopPunktekategorieDialogComponent, {
      modal: true,
      closable: true,
      focusOnShow: false,
      width: '600px',
      header: this.translate(punktekategorie ? 'CHOP.EDIT_PUNKTEKATEGORIE' : 'CHOP.CREATE_PUNKTEKATEGORIE'),
      data: { alterskategorieId: this.alterskategorieId, punktekategorie },
    });
  }

  private refreshBreadcrumbs(): void {
    this.setBreadcrumbs([
      { label: this.translate('MENU.CHOP'), routerLink: ['/admin-chop'] },
      {
        label: `${this.katalog.jahr} ${this.getLocalizedText(this.katalog.bezeichnung)}`,
        routerLink: ['/admin-chop/kataloge', this.katalog.id],
      },
      {
        label: `${this.alterskategorie.code} ${this.getLocalizedText(this.alterskategorie.bezeichnung)}`,
      },
    ]);
  }

  private setColumns(): void {
    this.leistungsgruppeColumns = [
      { field: 'nummer', headerKey: 'CHOP.NUMMER', width: '120px', filterType: 'integer' },
      { field: 'bezeichnung.De', headerKey: 'GLOBAL.NAME_De', minWidth: '200px' },
      { field: 'bezeichnung.Fr', headerKey: 'GLOBAL.NAME_Fr', minWidth: '200px' },
      { field: 'bezeichnung.It', headerKey: 'GLOBAL.NAME_It', minWidth: '200px' },
    ];
    this.punktekategorieColumns = [
      { field: 'code', headerKey: 'CHOP.CODE', width: '120px' },
      { field: 'bezeichnung.De', headerKey: 'GLOBAL.NAME_De', minWidth: '200px' },
      { field: 'bezeichnung.Fr', headerKey: 'GLOBAL.NAME_Fr', minWidth: '200px' },
      { field: 'bezeichnung.It', headerKey: 'GLOBAL.NAME_It', minWidth: '200px' },
      { field: 'punkteVon', headerKey: 'CHOP.PUNKTE_VON', width: '120px', filterType: 'integer' },
      { field: 'punkteBis', headerKey: 'CHOP.PUNKTE_BIS', width: '120px', filterType: 'integer' },
      { field: 'betrag', headerKey: 'CHOP.BETRAG', width: '140px', filterType: 'number' },
    ];
  }
}
